// src/pages/Onboarding/slides/UploadSlide.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import SlideShell from '../SlideShell';
import ImageUploadBox from '../../../components/Onboarding/ImageUploadBox';
import { setImageUploadArtifact } from '../../../utils/Onboarding/artifacts';
import { uploadImageToStorage } from '../../../utils/Onboarding/FirebaseHairAnalysis';
import { resizeImageToMaxLongEdge } from '../../../utils/Onboarding/ImageResize';
import { trackEvent } from '../../../utils/Tracking';
import { preloadAsset } from '../../../utils/Onboarding/assetPreloader';
import { detectFaces } from '../../../utils/Onboarding/MediaPipe';
import { clearHairgenJob } from '../../../utils/Onboarding/hairgenJobTracker';
// NOTE: startHairgenJob moved to ImageAnalysisSlide (triggers after analysis completes with metadata)

/**
 * UPLOAD SLIDE
 * 
 * Handles image file selection, client-side resize, Firebase Storage upload,
 * face validation, and artifact storage. Automatically advances to analysis slide.
 * 
 * RESPONSIBILITIES:
 * - File selection with mobile camera support
 * - Client-side resize (1600px max edge, JPEG 0.92 quality)
 * - Face detection validation (must detect at least one face)
 * - Firebase Storage upload with progress tracking
 * - Artifact storage (sessionStorage) for image blob
 * - Upload event tracking
 * - Auto-advance to ImageAnalysisSlide when ready
 * 
 * FLOW:
 * 1. User selects image → immediate validation & resize
 * 2. Run face detection - if no face, show error & reset
 * 3. Start Firebase upload in background
 * 4. Store blob in artifacts for next slide
 * 5. Auto-advance to ImageAnalysisSlide when upload completes
 */

export default function UploadSlide({
  title = 'Upload your selfie',
  subtitle = 'Take or upload a clear photo of your face and hair.',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  theme = 'light',
  onAutoSave = null,
  sessionId = null,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);

  // Preload the ImageAnalysisSlide background pattern on mount
  // This ensures it's ready before the user transitions to that slide
  useEffect(() => {
    preloadAsset(`${process.env.PUBLIC_URL}/logo_pattern_tile.png`, { priority: true });
  }, []);

  const handleUploadClick = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  }, []);

  const resetSlide = useCallback(() => {
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
  }, []);

  const handleFileChange = useCallback(async (event) => {
    const input = event.target;
    const file = input.files && input.files[0];
    if (!file) return;

    // Reset any previous error
    setError(null);
    setIsProcessing(true);
    setUploadProgress(0);

    // Clear any previous hairgen job (user is uploading new image)
    clearHairgenJob();

    // Track blob/dimensions at outer scope for error handling
    let processedBlob = null;
    let processedWidth = null;
    let processedHeight = null;

    try {
      // Step 1: Resize client-side (1600px max edge, JPEG 0.92)
      const { blob, width, height, mime } = await resizeImageToMaxLongEdge(
        file,
        1600,
        0.92,
        'image/jpeg'
      );
      
      // Store for potential error uploads
      processedBlob = blob;
      processedWidth = width;
      processedHeight = height;

      // Step 2: Face detection validation
      // Create temporary image for MediaPipe
      const img = new Image();
      const objUrl = URL.createObjectURL(blob);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = objUrl;
      });

      const faceResults = await detectFaces(img);
      URL.revokeObjectURL(objUrl); // Clean up

      if (!faceResults?.detections?.length) {
        // No face detected - upload faulty image for testing/debugging purposes
        // Use error-prefixed folder to distinguish from valid uploads
        let faultyImagePath = null;
        try {
          const { fileName: faultyFileName, uploadPromise: faultyUploadPromise } = uploadImageToStorage({
            blob,
            folder: 'image_analysis_errors/no_face_detected',
            fileExt: 'jpg',
            onProgress: () => {}, // Silent upload
          });
          await faultyUploadPromise;
          faultyImagePath = faultyFileName;
        } catch (uploadErr) {
          console.error('[UploadSlide] Failed to upload faulty image:', uploadErr);
        }

        // Show error and reset
        setError('No face detected. Please upload a clear photo of your face.');
        setIsProcessing(false);
        
        // Track error with faulty image path for debugging
        if (sessionId) {
          trackEvent('onboarding_checkpoint_image_uploaded', {
            onboarding_id: sessionId,
            had_error: true,
            error_type: 'no_face_detected',
            error_message: 'No face detected',
            faulty_image_path: faultyImagePath,
            image_dimensions: { width, height },
          }).catch(console.error);
        }

        // Auto-save faulty upload for record-keeping
        if (onAutoSave && faultyImagePath) {
          onAutoSave({
            faultyUploads: [{
              storagePath: faultyImagePath,
              errorType: 'no_face_detected',
              uploadedAt: new Date().toISOString(),
            }],
          });
        }
        
        input.value = '';
        return;
      }

      // Step 3: Store in artifacts immediately (for ImageAnalysisSlide)
      // Also clear any previous hairgen result to ensure fresh processing
      if (sessionId) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target.result;
          setImageUploadArtifact(sessionId, {
            imageDataUrl: dataUrl,
            imageWidth: width,
            imageHeight: height,
            imageMime: mime,
            // Clear previous hairgen result to force fresh generation
            hairgenResultUrl: null,
            hairgenProcessingTimeMs: null,
            hairgenTimestamp: null,
          });
        };
        reader.readAsDataURL(blob);
      }

      // Step 4: Start Firebase Storage upload
      const { fileName, uploadPromise } = uploadImageToStorage({
        blob,
        folder: 'image_analysis_test',
        fileExt: 'jpg',
        onProgress: (frac) => {
          setUploadProgress(Math.round(frac * 100));
        },
      });

      await uploadPromise;

      // Step 5: Store storage path in artifacts
      if (sessionId) {
        setImageUploadArtifact(sessionId, {
          firebaseStoragePath: fileName,
          uploadedAt: new Date().toISOString(),
        });
      }

      // NOTE: Hair generation is now triggered in ImageAnalysisSlide AFTER analysis completes.
      // This allows us to pass reportMetadata (analysisRequestId, scores, etc.) for report viewing.

      // Track successful upload
      if (sessionId) {
        trackEvent('onboarding_checkpoint_image_uploaded', {
          onboarding_id: sessionId,
          had_error: false,
          upload_info: {
            storagePath: fileName,
            uploadedAt: new Date().toISOString(),
          },
        }).catch(console.error);
      }

      // Auto-save upload event
      if (onAutoSave) {
        onAutoSave({
          selfieStorageLocations: [{
            storagePath: fileName,
            uploadedAt: new Date().toISOString(),
          }],
        });
      }

      // Step 6: Auto-advance to ImageAnalysisSlide
      // Pass imageUploadStatus: 'completed' to flip skipFlag to false
      onNext({
        firebaseStoragePath: fileName,
        imageUploaded: true,
      }, { imageUploadStatus: 'completed' });

    } catch (err) {
      console.error('[UploadSlide] Processing failed:', err);
      const errorMessage = err?.message || 'Image processing failed';
      
      // Determine error type for categorization
      let errorType = 'processing_failed';
      if (errorMessage.includes('load image')) {
        errorType = 'image_load_failed';
      } else if (errorMessage.includes('resize')) {
        errorType = 'resize_failed';
      } else if (errorMessage.includes('face')) {
        errorType = 'face_detection_failed';
      } else if (errorMessage.includes('upload')) {
        errorType = 'upload_failed';
      }
      
      // Try to upload faulty image if blob was created (for debugging)
      let faultyImagePath = null;
      if (processedBlob) {
        try {
          const { fileName: faultyFileName, uploadPromise: faultyUploadPromise } = uploadImageToStorage({
            blob: processedBlob,
            folder: `image_analysis_errors/${errorType}`,
            fileExt: 'jpg',
            onProgress: () => {},
          });
          await faultyUploadPromise;
          faultyImagePath = faultyFileName;
        } catch (uploadErr) {
          console.error('[UploadSlide] Failed to upload faulty image:', uploadErr);
        }
      }
      
      setError(errorMessage);
      setIsProcessing(false);

      // Track error with faulty image path for debugging
      if (sessionId) {
        trackEvent('onboarding_checkpoint_image_uploaded', {
          onboarding_id: sessionId,
          had_error: true,
          error_type: errorType,
          error_message: errorMessage,
          error_stack: err?.stack?.substring(0, 500), // First 500 chars of stack trace
          faulty_image_path: faultyImagePath,
          image_dimensions: processedWidth && processedHeight ? { 
            width: processedWidth, 
            height: processedHeight 
          } : null,
        }).catch(console.error);
      }

      // Auto-save faulty upload for record-keeping
      if (onAutoSave && faultyImagePath) {
        onAutoSave({
          faultyUploads: [{
            storagePath: faultyImagePath,
            errorType: errorType,
            errorMessage: errorMessage,
            uploadedAt: new Date().toISOString(),
          }],
        });
      }
    }

    input.value = '';
  }, [sessionId, onAutoSave, onNext]);

  const handleSkip = useCallback(() => {
    // Skip image upload - this will trigger the optional manual path
    onNext(null, { imageUploadStatus: 'skipped' });
  }, [onNext]);

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onBack={onBack}
      onNext={handleSkip}
      nextLabel="Skip →"
      disabled={isProcessing}
      theme={theme}
      scrollable={false}
      contentJustify="center"
      primaryVariant="skip"
    >
      <div className="w-full">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Always show the upload box */}
        <ImageUploadBox
          onUploadClick={handleUploadClick}
          theme={theme}
          disabled={isProcessing}
          isUploading={false}
          uploadProgress={0}
          error={error}
          uploadedFile={null}
        />

        {/* Loading overlay when processing */}
        {isProcessing && (
          <div className="mt-6 flex flex-col items-center">
            {/* Slim progress bar */}
            <div className="w-full max-w-xs h-1 bg-gray-200 rounded-full overflow-hidden mb-3">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${uploadProgress}%`,
                  backgroundColor: '#1f1f1f',
                }}
              />
            </div>
            
            {/* Please wait text with spinner */}
            <div className="flex items-center gap-2 text-gray-600">
              {/* Rotating spinner */}
              <svg 
                className="animate-spin h-4 w-4" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm">Please wait...</span>
            </div>
          </div>
        )}
      </div>
    </SlideShell>
  );
}
